// src/tattoo/imageUtils.js
function clamp01(v){ return Math.max(0, Math.min(1, v)); }

// super lightweight 3x3 box blur for alpha feather
function blurAlpha(alpha, w, h, passes = 1) {
  const a = alpha.slice();
  const out = new Uint8ClampedArray(a.length);
  const idx = (x,y) => y*w + x;
  for (let p=0; p<passes; p++){
    for (let y=0; y<h; y++){
      for (let x=0; x<w; x++){
        let sum=0, n=0;
        for (let j=-1; j<=1; j++){
          const yy = y+j; if (yy<0||yy>=h) continue;
          for (let i=-1; i<=1; i++){
            const xx = x+i; if (xx<0||xx>=w) continue;
            sum += a[idx(xx,yy)];
            n++;
          }
        }
        out[idx(x,y)] = (sum / n) | 0;
      }
    }
    a.set(out);
  }
  return a;
}

// optional 1px morphological erode to shave white fringe
function erodeAlpha(alpha, w, h, px=0){
  if (px<=0) return alpha;
  const a = alpha.slice();
  const out = new Uint8ClampedArray(a.length);
  const idx = (x,y) => y*w + x;
  for (let y=0; y<h; y++){
    for (let x=0; x<w; x++){
      let min = 255;
      for (let j=-px; j<=px; j++){
        const yy = y+j; if (yy<0||yy>=h) continue;
        for (let i=-px; i<=px; i++){
          const xx = x+i; if (xx<0||xx>=w) continue;
          const v = a[idx(xx,yy)];
          if (v < min) min = v;
        }
      }
      out[idx(x,y)] = min;
    }
  }
  return out;
}

/**
 * Remove white background from a JPEG and de-matte contaminated edges.
 * Returns a PNG dataURL with alpha.
 *
 * options:
 *  - threshold (0..255): how bright is considered "white-ish" (default 250)
 *  - softness  (0..255): feather into the edge (default 30)
 *  - defringe  (0..1):   how hard to remove white tint from colors (default 0.9)
 *  - erodePx   (0..2):   shave the mask by N pixels to kill a rim (default 1)
 */
export async function removeWhiteBackgroundPro(dataUrl, opts = {}) {
  const {
    threshold = 250,
    softness = 30,
    defringe = 0.4,
    erodePx = 1,
  } = opts;

  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = dataUrl;
  });

  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;

  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);

  const imgData = ctx.getImageData(0, 0, w, h);
  const d = imgData.data;

  // Step 1: estimate alpha: white → transparent, darker → opaque
  // Use max channel (distance to white) with smoothstep around threshold.
  const lo = Math.max(0, threshold - softness);
  const hi = threshold;
  const smoothstep = (a,b,x) => {
    const t = clamp01((x - a) / Math.max(1e-6, b - a));
    return t * t * (3 - 2*t);
  };

  // Raw alpha (0..255)
  const rawA = new Uint8ClampedArray(w*h);
  for (let i=0, p=0; i<d.length; i+=4, p++){
    const r = d[i], g = d[i+1], b = d[i+2];
    const maxc = Math.max(r,g,b);  // how close to white
    // t: 0 = keep, 1 = remove (white)
    const t = smoothstep(lo, hi, maxc);
    const a = (1 - t) * 255;
    rawA[p] = a < 0 ? 0 : a > 255 ? 255 : a|0;
  }

  // Optional erode to shave the bright fringe, then a tiny blur/feather
  let A = erodeAlpha(rawA, w, h, erodePx);
  A = blurAlpha(A, w, h, 1);

  // Step 2: de-matte the colors (unmix from white)
  // Compositing model with matte=white:
  // C = a*Fg + (1-a)*1  =>  Fg = (C - (1-a)*1) / a   for a>0
  for (let i=0, p=0; i<d.length; i+=4, p++){
    const a = A[p] / 255;
    if (a <= 0.001) {
      d[i] = d[i+1] = d[i+2] = 0; // fully transparent
      d[i+3] = 0;
      continue;
    }
    // un-matte against white
    let r = (d[i]   /255 - (1 - a)) / a;
    let g = (d[i+1]/255 - (1 - a)) / a;
    let b = (d[i+2]/255 - (1 - a)) / a;

    // optional defringe: pull remaining tint away from white
    // (makes semi-transparent edge pixels less milky)
    const k = defringe * (1 - a); // stronger where more transparent
    r = r*(1 - k); g = g*(1 - k); b = b*(1 - k);

    // clamp & write back as premultiplied by new alpha
    r = clamp01(r); g = clamp01(g); b = clamp01(b);
    d[i]   = (r * 255 * a) | 0;
    d[i+1] = (g * 255 * a) | 0;
    d[i+2] = (b * 255 * a) | 0;
    d[i+3] = (a * 255) | 0;
  }

  ctx.putImageData(imgData, 0, 0);
  return canvas.toDataURL("image/png");
}
// src/tattoo/imageUtils.js
export async function featherAndBleedAlpha(dataUrl, {
    radius = 2,      // px blur (1.5–3 looks good)
    grow = -0.75,    // negative erodes, positive dilates (in px)
    neutralRGB = 255 // 255=white (best for multiply), 0=black (for normal blend)
  } = {}) {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.crossOrigin = "anonymous";
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = dataUrl;
    });
  
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
  
    // base
    const c1 = document.createElement("canvas");
    c1.width = w; c1.height = h;
    const g1 = c1.getContext("2d");
    g1.drawImage(img, 0, 0);
    const base = g1.getImageData(0, 0, w, h);
  
    // --- build a mask (current alpha), blur it, then grow/shrink ---
    const mask = new Uint8ClampedArray(w * h);
    for (let i = 0, j = 3; i < mask.length; i++, j += 4) mask[i] = base.data[j];
  
    // separable box blur (fast & good enough)
    function blur1D(arr, out, n, passes = 2) {
      const tmp = new Uint8ClampedArray(n);
      let src = arr, dst = tmp;
      const R = Math.max(1, Math.round(radius));
      const W = w, H = h;
  
      // horizontal
      for (let p = 0; p < passes; p++) {
        let sum = 0;
        for (let y = 0; y < H; y++) {
          sum = 0;
          let idx = y * W;
          for (let x = -R; x <= R; x++) sum += src[idx + Math.min(W - 1, Math.max(0, x))];
          for (let x = 0; x < W; x++) {
            dst[idx + x] = Math.round(sum / (2 * R + 1));
            const add = x + R + 1;
            const sub = x - R;
            sum += src[idx + Math.min(W - 1, add)];
            sum -= src[idx + Math.max(0, sub)];
          }
        }
        [src, dst] = [dst, src]; // swap
      }
  
      // vertical
      for (let p = 0; p < passes; p++) {
        let sum = 0;
        for (let x = 0; x < W; x++) {
          sum = 0;
          for (let y = -R; y <= R; y++) sum += src[Math.min(H - 1, Math.max(0, y)) * W + x];
          for (let y = 0; y < H; y++) {
            dst[y * W + x] = Math.round(sum / (2 * R + 1));
            const add = y + R + 1;
            const sub = y - R;
            sum += src[Math.min(H - 1, add) * W + x];
            sum -= src[Math.max(0, sub) * W + x];
          }
        }
        [src, dst] = [dst, src];
      }
      out.set(src);
    }
  
    const blurred = new Uint8ClampedArray(mask.length);
    blur1D(mask, blurred, mask.length, 2);
  
    // grow/shrink by offsetting the blurred mask’s threshold
    // convert grow px into ~alpha units using radius
    const offset = Math.round(grow * 255 / Math.max(1, radius * 2.5));
    for (let i = 0; i < blurred.length; i++) {
      let a = blurred[i] + offset;
      if (a < 0) a = 0;
      if (a > 255) a = 255;
      blurred[i] = a;
    }
  
    // --- write back alpha, and bleed RGB to neutral where alpha is low ---
    const out = g1.createImageData(w, h);
    out.data.set(base.data);
  
    for (let i = 0, p = 0; i < blurred.length; i++, p += 4) {
      const a = blurred[i];
      out.data[p + 3] = a;
  
      // bleed color under transparency to avoid dark fringe in multiply
      if (a < 250) {
        out.data[p + 0] = neutralRGB;
        out.data[p + 1] = neutralRGB;
        out.data[p + 2] = neutralRGB;
      }
    }
  
    g1.putImageData(out, 0, 0);
    return c1.toDataURL("image/png");
  }